import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";
import { ArrowRight, Truck, Sparkles, Users, Gift } from "lucide-react";
import { Helmet } from "react-helmet-async";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const perks = [
  {
    icon: <Truck className="h-8 w-8 text-yellow-500" />,
    title: "Fast & Global Delivery",
    description: "From Nanyuki to the world – get your order wherever you are!",
  },
  {
    icon: <Sparkles className="h-8 w-8 text-yellow-500" />,
    title: "New Styles Weekly",
    description: "Fresh drops every week. Follow us @pakfashions to stay trendy.",
  },
  {
    icon: <Users className="h-8 w-8 text-yellow-500" />,
    title: "Loved by Fashionistas",
    description: "Hundreds of happy icons across Kenya and beyond!",
  },
  {
    icon: <Gift className="h-8 w-8 text-yellow-500" />,
    title: "Give the Gift of Style",
    description: "We offer stylish gift cards for your loved ones.",
  },
];

function Home() {
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSeller, setBestSeller] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [
        featuredRes,
        testimonialsRes,
        newArrivalsRes,
        bestSellerRes,
      ] = await Promise.all([
        axios.get(`${SERVER_URL}/api/products?category=Clearance Sale&limit=4`),
        axios.get(`${SERVER_URL}/api/testimonials`),
        axios.get(`${SERVER_URL}/api/products?tag=New Arrival&limit=4`),
        axios.get(`${SERVER_URL}/api/products?tag=Bestseller&limit=4`),
      ]);

      setFeatured(featuredRes.data);
      setTestimonials(testimonialsRes.data);
      setNewArrivals(newArrivalsRes.data);
      setBestSeller(bestSellerRes.data);
    } catch (error) {
      console.error("Failed to fetch homepage data:", error);
    } finally {
      setLoading(false);
    }
  };

  const ProductSkeleton = () => (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col justify-between">
      <div className="w-full h-64 bg-gray-200 animate-pulse rounded-xl mb-4"></div>
      <div className="space-y-3 px-1">
        <div className="w-3/4 h-6 bg-gray-200 animate-pulse rounded"></div>
        <div className="w-1/2 h-4 bg-gray-200 animate-pulse rounded"></div>
        <div className="w-1/3 h-5 bg-gray-200 animate-pulse rounded mt-2"></div>
      </div>
    </div>
  );

  const renderProductGrid = (items) => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 max-w-7xl mx-auto px-4">
          {[1, 2, 3, 4].map((n) => <ProductSkeleton key={n} />)}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 max-w-7xl mx-auto px-4">
        {items.map((item, index) => (
          <Link to={`/product/${item._id}`} key={item._id}>
            <div
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="h-64 overflow-hidden rounded-xl mb-4 relative bg-gray-50">
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="px-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1 truncate group-hover:text-yellow-600 transition-colors">
                  {item.name}
                </h3>
                <p className="text-gray-500 mb-3 text-sm line-clamp-1">
                  {item.description}
                </p>
                <span className="text-black font-extrabold text-lg">
                  Ksh {item.price.toLocaleString()}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Pak Fashions | Stylish, Affordable Clothing in Kenya</title>
        <meta
          name="description"
          content="Shop Pak Fashions for elegant, affordable men's, women's, and kids' clothing, shoes, bags & accessories. Based in Nanyuki, Laikipia."
        />
        <meta
          name="keywords"
          content="pak fashions, stylish clothes, affordable fashion Kenya, Nanyuki fashion, Laikipia fashion, elegant wear, thrift fashion"
        />
        <meta name="author" content="Pak Fashions developer (~frank)" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph for Facebook, WhatsApp */}
        <meta property="og:title" content="Pak Fashions | Unleash Your Elegance" />
        <meta property="og:description" content="Affordable, trendy fashion for men, women, and kids. Located in Nanyuki 🌸 Fast delivery. 🌍" />
        <meta property="og:image" content="https://pakfashions.co.ke/PakFashions-logo.jpg" />
        <meta property="og:url" content="https://pakfashions.co.ke" />
        <meta property="og:type" content="website" />
      </Helmet>

      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <section
          className="relative flex flex-col justify-center items-center text-center px-6 h-[60vh] md:h-[90vh] bg-gray-900 border-b border-gray-200 mt-[60px] md:mt-[70px] overflow-hidden"
        >
          {/* Animated Background Images Array or CSS Background */}
          {/* We use a combination of multiple background images with slow zoom (ken burns effect) and a glass overlay */}
          <div className="absolute inset-0 z-0">
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat animate-boutique-pan opacity-60"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?auto=format&fit=crop&w=1920&q=80')`
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
            
            {/* Animated Light Orbs for that "Luxury Glass" feel */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-400/20 rounded-full blur-[100px] animate-blob"></div>
            <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-yellow-600/20 rounded-full blur-[100px] animate-blob [animation-delay:2000ms]"></div>
            <div className="absolute bottom-1/4 left-1/2 w-96 h-96 bg-white/10 rounded-full blur-[100px] animate-blob [animation-delay:4000ms]"></div>
          </div>

          <div className="relative z-10 space-y-8 flex flex-col items-center">
            {/* Elegant glasspill for 'New Collection' */}
            <div 
              className="px-6 py-2 rounded-full border border-yellow-400/30 bg-white/5 backdrop-blur-md text-yellow-400 text-sm md:text-base font-bold tracking-widest uppercase shadow-lg mb-4"
              data-aos="fade-down"
            >
              The 2026 Collection
            </div>
            
            <h1
              className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-white tracking-tight leading-[1.1] max-w-4xl mx-auto drop-shadow-2xl"
              data-aos="fade-up"
            >
              Where style meets <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-500 to-yellow-300 animate-pulse-slow">every generation</span>
            </h1>
            <p
              className="text-lg md:text-2xl text-gray-300 font-medium max-w-2xl mx-auto"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              Fashion that fits you and your budget. Curated for the modern, elegant wardrobe in Nanyuki.
            </p>
            <div data-aos="fade-up" data-aos-delay="200" className="pt-8">
              <Link to="/shop">
                <button className="bg-white text-black font-extrabold px-12 py-5 rounded-full shadow-[0_10px_40px_rgba(234,179,8,0.3)] hover:shadow-[0_10px_50px_rgba(234,179,8,0.5)] hover:bg-yellow-50 transform hover:-translate-y-1 transition-all duration-300 focus:outline-none flex items-center gap-3">
                  Shop The Collection <ArrowRight className="w-5 h-5 text-yellow-500" />
                </button>
              </Link>
            </div>
          </div>
        </section>

        {/* New Arrivals */}
        <section className="bg-white py-24 px-6 relative">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-end mb-12">
              <div>
                <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight" data-aos="fade-right">
                  New Arrivals
                </h2>
                <div className="h-1 w-16 bg-yellow-400 mt-2 rounded-full" data-aos="fade-right" data-aos-delay="100"></div>
              </div>
              <Link to="/shop" className="text-gray-600 hover:text-yellow-600 font-semibold flex items-center transition-colors">
                View All <ArrowRight size={18} className="ml-1" />
              </Link>
            </div>
            {renderProductGrid(newArrivals)}
          </div>
        </section>

        {/* Featured / Clearance */}
        <section className="bg-gray-50/50 py-24 px-6 border-y border-gray-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-end mb-12">
              <div>
                <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight" data-aos="fade-right">
                  Sale Up to 50% Off
                </h2>
                <div className="h-1 w-16 bg-yellow-400 mt-2 rounded-full" data-aos="fade-right" data-aos-delay="100"></div>
              </div>
              <Link to="/shop" className="text-gray-600 hover:text-yellow-600 font-semibold flex items-center transition-colors">
                Shop Sale <ArrowRight size={18} className="ml-1" />
              </Link>
            </div>
            {renderProductGrid(featured)}
          </div>
        </section>

        {/* Best Sellers */}
        <section className="bg-white py-24 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-end mb-12">
              <div>
                <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight" data-aos="fade-right">
                  Best Sellers
                </h2>
                <div className="h-1 w-16 bg-yellow-400 mt-2 rounded-full" data-aos="fade-right" data-aos-delay="100"></div>
              </div>
              <Link to="/shop" className="text-gray-600 hover:text-yellow-600 font-semibold flex items-center transition-colors">
                View Bestsellers <ArrowRight size={18} className="ml-1" />
              </Link>
            </div>
            {renderProductGrid(bestSeller)}
            
            {!loading && (
              <div className="text-center mt-16 pb-8" data-aos="fade-up">
                <Link to="/shop">
                  <button className="bg-gray-900 text-white font-semibold px-10 py-4 rounded-full shadow-md hover:bg-gray-800 hover:shadow-lg transition-all">
                    Explore All Collections
                  </button>
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Why Shop With Us? */}
        <section className="bg-white py-20 px-6 border-t border-gray-100">
          <div className="max-w-6xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight" data-aos="fade-up">
              The Pak Fashions Experience
            </h2>
            <div className="h-1 w-20 bg-yellow-400 mt-4 rounded-full mx-auto" data-aos="fade-up" data-aos-delay="100"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {perks.map((perk, idx) => (
              <div
                key={idx}
                className="bg-gray-50 p-8 rounded-3xl border border-gray-100 text-center transition duration-500 hover:shadow-md hover:bg-white"
                data-aos="fade-up"
                data-aos-delay={idx * 150}
              >
                <div className="flex justify-center mb-5 bg-yellow-100 w-16 h-16 mx-auto items-center rounded-2xl">{perk.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {perk.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">{perk.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-yellow-50 py-24 px-6 border-t border-yellow-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight" data-aos="fade-up">
                What Our Customers Say
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {!loading && testimonials.slice(0, 2).map((testimonial, index) => (
                <div
                  key={testimonial._id}
                  className="bg-white p-8 rounded-3xl shadow-sm border border-yellow-200/50 flex flex-col justify-between"
                  data-aos="fade-up"
                  data-aos-delay={index * 150}
                >
                  <div>
                    <div className="text-yellow-400 mb-4 flex gap-1">
                      {[...Array(5)].map((_, i) => (<span key={i}>★</span>))}
                    </div>
                    <p className="text-gray-700 font-medium mb-6 leading-relaxed italic">
                      "{testimonial.message}"
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center font-bold text-yellow-700">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">
                        {testimonial.name}
                      </div>
                      <span className="text-gray-500 text-xs font-semibold uppercase tracking-wider">Verified Buyer</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <Link
                to={`/testimonials`}
                className="inline-flex items-center text-gray-600 font-semibold hover:text-yellow-600 transition-colors"
              >
                Read More Reviews <ArrowRight className="ml-2" size={18} />
              </Link>
            </div>
          </div>
        </section>

      </main>
    </>
  );
}

export default Home;
